import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { generateCSAWCertificatePDF } from '@/utils/pdfGenerator';
import { gql, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { Eye, FileText, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import COVCertificateTemplate from '../../pages/Personnel/components/CertificateComponents/certificateTemplates/COVCertificateTemplate';
import CSAWCertificateTemplate from '../../pages/Personnel/components/CertificateComponents/certificateTemplates/CSAWCertificateTemplate';
import PLTCPCertificateTemplate from '../../pages/Personnel/components/CertificateComponents/certificateTemplates/PLTCPCertificateTemplate';
import PLTPCertificateTemplate from '../../pages/Personnel/components/CertificateComponents/certificateTemplates/PLTPCertificateTemplate';
import PTPRCertificateTemplate from '../../pages/Personnel/components/CertificateComponents/certificateTemplates/PTPRCertificateTemplate';
import TCEBPCertificateTemplate from '../../pages/Personnel/components/CertificateComponents/certificateTemplates/TCEBPCertificateTemplate';

const GET_CERTIFICATE_DETAILS = gql`
   query GetCertificateDetails($id: ID!) {
      getCertificateById(id: $id) {
         id
         certificateNumber
         certificateStatus
         dateIssued
         expiryDate
         dateApproved
         forestRangerSignature {
            data
            contentType
         }
         chiefTSDSignature {
            data
            contentType
         }
         penrCenrOfficerSignature {
            data
            contentType
         }
         applicantSignature {
            data
            contentType
         }
         applicationType
         csawCertificateData {
            registrationType
            ownerName
            address
            purpose
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         ptprCertificateData {
            ownerName
            address
            contactNumber
            email
            octTctNumber
            lotNumber
            barangay
            municipality
            province
            totalLotArea
            areaDevotedToPlantation
            spacing
            treeSpeciesPlanted
            datePlanted
         }
         pltpCertificateData {
            name
            address
            lotNumber
            octNumber
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               treeStatus
               grossVolume
               netVolume
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         pltcpCertificateData {
            name
            occupation
            projectLocation
            projectAddress
            purpose
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeCategory
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         tcebpCertificateData {
            name
            officeName
            officeAddress
            projectLocation
            contactNumber
            purpose
            requestType
            treeInventory {
               species
               numberOfTreesInventoried
               status
               treeCategory
            }
            totalTrees
         }
         orderOfPayment {
            id
            OOPstatus
            officialReceipt {
               orNumber
               dateIssued
               amount
               paymentMethod
               remarks
            }
         }
      }
   }
`;

const CertificateViewModal = ({ isOpen, onClose, certificate, application, loading, error }) => {
   const [previewUrl, setPreviewUrl] = useState(null);
   const [showECertificate, setShowECertificate] = useState(false);
   const certificateRef = useRef();
   const navigate = useNavigate();
   const { data: certDetails, loading: detailsLoading } = useQuery(GET_CERTIFICATE_DETAILS, {
      variables: { id: certificate?.id },
      skip: !certificate?.id,
      fetchPolicy: 'network-only',
      pollInterval: 10000
   });

   useEffect(() => {
      console.log('Certificate Details:', certDetails);
   }, [certDetails]);

   useEffect(() => {
      return () => {
         if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
         }
      };
   }, [previewUrl]);

   const renderCertificateTemplate = () => {
      const certData = certDetails?.getCertificateById || certificate;

      if (!certData) {
         return null;
      }

      switch (certData.applicationType) {
         case 'Chainsaw Registration':
            return (
               <CSAWCertificateTemplate
                  ref={certificateRef}
                  certificate={certData}
                  application={certData.csawCertificateData}
                  orderOfPayment={certData.orderOfPayment}
                  hiddenOnPrint={[]}
               />
            );
         case 'Certificate of Verification':
            if (!certData.covCertificateData) {
               return null;
            }

            return (
               <COVCertificateTemplate
                  ref={certificateRef}
                  certificate={certData}
                  application={certData.covCertificateData}
                  orderOfPayment={certData.orderOfPayment}
                  hiddenOnPrint={[]}
               />
            );
         case 'Private Tree Plantation Registration':
            if (!certData.ptprCertificateData) {
               return null;
            }

            return (
               <PTPRCertificateTemplate
                  ref={certificateRef}
                  certificate={certData}
                  application={certData.ptprCertificateData}
                  orderOfPayment={certData.orderOfPayment}
                  hiddenOnPrint={[]}
               />
            );
         case 'Public Land Tree Cutting Permit':
            if (!certData.pltcpCertificateData) {
               return null;
            }

            return (
               <PLTCPCertificateTemplate
                  ref={certificateRef}
                  certificate={certData}
                  application={certData.pltcpCertificateData}
                  orderOfPayment={certData.orderOfPayment}
                  hiddenOnPrint={[]}
               />
            );
         case 'Private Land Timber Permit':
            if (!certData.pltpCertificateData) {
               return null;
            }

            return (
               <PLTPCertificateTemplate
                  ref={certificateRef}
                  certificate={certData}
                  application={certData.pltpCertificateData}
                  orderOfPayment={certData.orderOfPayment}
                  hiddenOnPrint={[]}
               />
            );
         case 'Tree Cutting and/or Earth Balling Permit':
            console.log('Raw Certificate Data:', certData); // Debug log

            if (!certData.tcebpCertificateData) {
               console.error('Missing tcebpCertificateData in certificate:', certData);
               return null;
            }

            return (
               <TCEBPCertificateTemplate
                  ref={certificateRef}
                  certificate={certData}
                  application={certData.tcebpCertificateData}
                  orderOfPayment={certData.orderOfPayment}
                  hiddenOnPrint={[]}
               />
            );
         default:
            return null;
      }
   };

   const handlePrint = () => {
      const certData = certDetails?.getCertificateById || certificate;
      console.log('Certificate Data:', certData); // For debugging

      navigate('/personnel/csaw-certificate-print', {
         state: {
            certificate: {
               ...certData,
               certificateData: certData.csawCertificateData || {},
               certificateNumber: certData.certificateNumber,
               dateIssued: certData.dateIssued,
               expiryDate: certData.expiryDate,
               signature: {
                  data: certData.penrCenrOfficerSignature?.data,
                  contentType: certData.penrCenrOfficerSignature?.contentType
               },
               orderOfPayment: certData.orderOfPayment
            },
            orderOfPayment: certData.orderOfPayment
         }
      });
   };

   const handlePrintTerms = () => {
      navigate('/personnel/csaw-terms-print');
   };

   const handleDownload = async () => {
      try {
         const pdfBlob = await generateCSAWCertificatePDF({
            certificate: certificate
         });

         const url = window.URL.createObjectURL(pdfBlob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `Certificate-${certificate.certificateNumber}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);

         toast.success('Certificate downloaded successfully');
      } catch (error) {
         console.error('Error downloading certificate:', error);
         toast.error('Failed to download certificate');
      }
   };

   if (loading || detailsLoading) {
      return (
         <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md" aria-describedby="loading-description">
               <DialogHeader>
                  <DialogTitle className="sr-only">Loading Certificate Details</DialogTitle>
               </DialogHeader>
               <div className="flex items-center justify-center p-6">
                  <Loader2 className="h-6 w-6 animate-spin" />
               </div>
               <DialogDescription id="loading-description" className="sr-only">
                  Loading certificate details, please wait...
               </DialogDescription>
            </DialogContent>
         </Dialog>
      );
   }

   if (error) {
      return (
         <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md" aria-describedby="error-description">
               <DialogHeader>
                  <DialogTitle>Error Loading Certificate</DialogTitle>
                  <DialogDescription id="error-description">{error.message}</DialogDescription>
               </DialogHeader>
            </DialogContent>
         </Dialog>
      );
   }

   if (!certificate) return null;

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent
            className={showECertificate ? "max-w-4xl" : "max-w-md"}
            aria-describedby={showECertificate ? "preview-description" : "details-description"}
         >
            {showECertificate ? (
               <>
                  <DialogHeader>
                     <DialogTitle>E-Certificate Preview</DialogTitle>
                     <DialogDescription id="preview-description">
                        Preview of the electronic certificate
                     </DialogDescription>
                  </DialogHeader>

                  <div className="overflow-auto max-h-[70vh] bg-white dark:bg-gray-900 rounded-lg">
                     {renderCertificateTemplate()}
                  </div>

                  <div className="flex justify-end gap-2">
                     <Button variant="outline" onClick={() => setShowECertificate(false)}>
                        Back
                     </Button>
                     <Button
                        onClick={handlePrint}
                        className="bg-blue-600 hover:bg-blue-700"
                     >
                        <FileText className="h-4 w-4 mr-2" />
                        Print Certificate
                     </Button>
                     {/* <Button
                        onClick={handleDownload}
                        variant="outline"
                        className="text-green-600 hover:text-green-700"
                     >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                     </Button> */}
                     {certDetails?.getCertificateById?.applicationType === 'Chainsaw Registration' && (
                        <Button
                           onClick={handlePrintTerms}
                           variant="outline"
                        >
                           <FileText className="h-4 w-4 mr-2" />
                           Print Terms
                        </Button>
                     )}
                  </div>
               </>
            ) : (
               <>
                  <DialogHeader>
                     <DialogTitle>Certificate Details</DialogTitle>
                     <DialogDescription id="details-description">
                        Certificate Number: {certificate.certificateNumber}
                     </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-6">
                     {/* Certificate Type Section */}
                     {/* <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Certificate Type</h3>
                        <p>{certificate.applicationType}</p>
                     </div> */}

                     <Separator />

                     {/* Issue Details Section */}
                     <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-2">Issue Details</h3>
                        <div className="space-y-2">
                           {certificate.dateIssued && (
                              <div>
                                 <span className="text-sm">Date Issued: </span>
                                 <span className="text-sm">
                                    {format(new Date(parseInt(certificate.dateIssued)), 'PPP')}
                                 </span>
                              </div>
                           )}
                           {certificate.expiryDate && (
                              <div>
                                 <span className="text-sm">Expiry Date: </span>
                                 <span className="text-sm">
                                    {format(new Date(parseInt(certificate.expiryDate)), 'PPP')}
                                 </span>
                              </div>
                           )}
                        </div>
                     </div>

                     <Separator />

                     {/* Status Section */}
                     <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">Status</span>
                        <Badge variant={certificate.certificateStatus === 'Active' ? 'success' : 'secondary'}>
                           {certificate.certificateStatus}
                        </Badge>
                     </div>

                     <Separator />

                     {/* Actions Section */}
                     <div className="space-y-4 pb-2">
                        <Button
                           className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           onClick={() => setShowECertificate(true)}
                        >
                           <Eye className="h-4 w-4 mr-2" />
                           View Certificate
                        </Button>

                        {/* <div className="grid grid-cols-2 gap-2">
                           <Button
                              className="w-full"
                              onClick={handlePrint}
                              variant="outline"
                           >
                              <Download className="h-4 w-4 mr-2" />
                              Print Certificate
                           </Button>
                           <Button
                              className="w-full"
                              onClick={handlePrintTerms}
                              variant="outline"
                           >
                              <Download className="h-4 w-4 mr-2" />
                              Print Terms
                           </Button>
                        </div> */}
                     </div>
                  </div>

                  {/* <DialogFooter>
                     <Button variant="outline" onClick={onClose}>
                        Close
                     </Button>
                  </DialogFooter> */}
               </>
            )}
         </DialogContent>
      </Dialog>
   );
};

export default CertificateViewModal;
